import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  Grid,
  Divider,
  Paper,
  Avatar,
  alpha,
  useTheme
} from '@mui/material';
import {
  Close as CloseIcon,
  SettingsEthernet as CircuitIcon,
  LocationOn as LocationIcon,
  Router as RouterIcon,
  PersonOutline as PersonIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Assignment as ContractIcon,
  Person as ClientIcon
} from '@mui/icons-material';
import circuitService, { Circuito } from '../../services/circuitService';

interface CircuitoViewModalProps {
  open: boolean;
  onClose: () => void;
  circuitoId: number;
}

const InfoItem = ({ icon: Icon, label, value, subValue }: { icon: any, label: string, value: React.ReactNode, subValue?: React.ReactNode }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
      <Box sx={{
        mr: 1.5,
        p: 1,
        borderRadius: 1.5,
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
        display: 'flex'
      }}>
        <Icon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
      </Box>
      <Box sx={{ flex: 1, pt: 0.2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5, fontSize: '0.65rem' }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mt: 0.2, lineHeight: 1.2 }}>
          {value}
        </Typography>
        {subValue && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.2, display: 'block' }}>
            {subValue}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const CircuitoViewModal: React.FC<CircuitoViewModalProps> = ({
  open,
  onClose,
  circuitoId
}) => {
  const theme = useTheme();
  const [circuito, setCircuito] = useState<Circuito | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && circuitoId) {
      setLoading(true);
      circuitService.getCircuito(circuitoId)
        .then((data) => {
          const circuitoData = (data as any).data || (data as any).circuito || data;
          setCircuito(circuitoData);
        })
        .catch((error) => console.error('Erro ao buscar circuito:', error))
        .finally(() => setLoading(false));
    } else {
      setCircuito(null);
    }
  }, [open, circuitoId]);

  const handleClose = () => {
    onClose();
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
          <Typography>Carregando dados do circuito...</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  if (!circuito) return null;

  const isLivre = circuito.situacao === 'livre';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 24px 48px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }
      }}
    >
      <Box sx={{
        px: 2,
        py: 1,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.light, 0.15)} 100%)`,
        borderBottom: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{
            bgcolor: 'primary.main',
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
          }}>
            <CircuitIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              Detalhes do Circuito
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              ID do Sistema: {circuito.id || 'N/A'}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{
          bgcolor: 'background.paper',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          '&:hover': { bgcolor: 'background.default' }
        }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 1 }}>
          <Paper elevation={0} sx={{
            p: 1,
            mb: 2,
            borderRadius: 3,
            bgcolor: alpha(theme.palette.background.default, 0.5),
            border: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>
                Identificação
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 900, color: 'primary.main' }}>
                {circuito.identificacao}
              </Typography>
            </Box>
            <Chip
              icon={isLivre ? <CheckCircleIcon /> : <CancelIcon />}
              label={isLivre ? 'Livre' : 'Ocupado'}
              color={isLivre ? 'success' : 'error'}
              variant={isLivre ? "outlined" : "filled"}
              sx={{
                fontWeight: 800,
                px: 0.2,
                py: 1,
                borderRadius: 2,
                fontSize: '0.8rem',
                borderWidth: isLivre ? 2 : 0,
              }}
            />
          </Paper>

          <Grid container spacing={1}>
            <Grid item xs={12}>
              <InfoItem
                icon={LocationIcon}
                label="Endereço de Instalação"
                value={circuito.endereco || 'Endereço não informado'}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 0, borderStyle: 'dashed' }} />
            </Grid>
            <Grid item xs={12}>
              <InfoItem
                icon={RouterIcon}
                label="Caixa de Atendimento"
                value={circuito.caixa?.name || 'Não vinculada'}
                subValue={circuito.caixa?.address}
              />
            </Grid>

            {/* Exibe o contrato vinculado, se houver */}
            {circuito.contrato && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 0, borderStyle: 'dashed' }} />
                </Grid>
                <Grid item xs={12} sm={7}>
                  <InfoItem
                    icon={ClientIcon}
                    label="Cliente"
                    value={circuito.contrato.cliente?.nome || 'N/A'}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <InfoItem
                    icon={ContractIcon}
                    label="Contrato Vinculado"
                    value={circuito.contrato.titulo || `Contrato #${circuito.contrato.id}`}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <Divider sx={{ my: 0, borderStyle: 'dashed' }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem
                icon={PersonIcon}
                label="Criado por"
                value={circuito.usuario_criou?.nome || 'Sistema'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem
                icon={CalendarIcon}
                label="Data de Criação"
                value={new Date(circuito.createdAt).toLocaleDateString('pt-BR')}
                subValue={new Date(circuito.createdAt).toLocaleTimeString('pt-BR')}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 1, py: 1, borderTop: `1px solid ${theme.palette.divider}`, bgcolor: alpha(theme.palette.background.default, 0.4) }}>
        <Button
          onClick={handleClose}
          variant="contained"
          disableElevation
          sx={{
            px: 1,
            py: 0.5,
            borderRadius: 2,
            fontWeight: 700,
            textTransform: 'none',
            fontSize: '0.95rem',
            color: 'text.primary',
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
            '&:hover': {
              bgcolor: 'background.default',
            }
          }}
        >
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CircuitoViewModal; 